import React, { useEffect, useMemo, useState } from "react";
import { Button, ConfigProvider, Empty, Modal, Spin, ThemeConfig } from "antd";
import useLang from "../../../hooks/useLang";
import {
	DriverSalaryPayoutResult,
	getPayableDriverBalances,
	PayableDriverBalance,
	payDriverBalances,
} from "../../../api/driver-salary-payouts";
import { BRAND_PRIMARY } from "../../BulkOrderOperations/components/styles";
import PayoutConfigureStep from "./PayoutConfigureStep";
import PayoutResultStep from "./PayoutResultStep";
import PayoutReviewStep from "./PayoutReviewStep";
import { sumAmounts } from "./payoutFormat";

type PayoutStep = "configure" | "review" | "result";

/**
 * Scoped to this modal only: slightly larger text and 40px controls (antd's default is 14px /
 * 32px), and the brand colour for primary buttons so they need no inline style.
 */
const PAYOUT_MODAL_THEME: ThemeConfig = {
	token: {
		colorPrimary: BRAND_PRIMARY,
		fontSize: 15,
		controlHeight: 40,
	},
};

interface BulkDriverPayoutModalProps {
	open: boolean;
	onClose: () => void;
	/** Called on close when at least one driver was paid, so the grid can refresh its balances. */
	onPaid: () => void;
}

/**
 * "Barazo Shoferat": pays every driver with a positive salary balance, minus the ones the admin
 * excludes, with one shared description. Configure → review → result.
 */
const BulkDriverPayoutModal = ({
	open,
	onClose,
	onPaid,
}: BulkDriverPayoutModalProps) => {
	const { strings } = useLang("driver");
	const copy = strings?.bulkPayout;

	const [drivers, setDrivers] = useState<PayableDriverBalance[]>([]);
	const [loading, setLoading] = useState(false);
	const [excludedIds, setExcludedIds] = useState<number[]>([]);
	const [description, setDescription] = useState("");
	const [step, setStep] = useState<PayoutStep>("configure");
	const [submitting, setSubmitting] = useState(false);
	const [result, setResult] = useState<DriverSalaryPayoutResult | null>(null);

	// Re-read on every open: balances move all day, and a list from an earlier open could pay
	// someone the wrong amount or miss someone new.
	useEffect(() => {
		if (!open) return;

		let cancelled = false;
		setExcludedIds([]);
		setDescription("");
		setStep("configure");
		setResult(null);
		setDrivers([]);
		setLoading(true);

		getPayableDriverBalances()
			.then((payable) => {
				if (!cancelled) setDrivers(payable);
			})
			.catch(() => {
				// The axios interceptor already toasted the error; the modal shows the empty state.
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});

		return () => {
			cancelled = true;
		};
	}, [open]);

	const driverById = useMemo(
		() => new Map(drivers.map((driver) => [driver.driverId, driver])),
		[drivers]
	);
	const previewBalances = useMemo(
		() =>
			new Map(drivers.map((driver) => [driver.driverId, driver.balance])),
		[drivers]
	);
	const included = useMemo(() => {
		const excludedSet = new Set(excludedIds);
		return drivers.filter((driver) => !excludedSet.has(driver.driverId));
	}, [drivers, excludedIds]);
	const excluded = useMemo(
		() =>
			excludedIds
				.map((id) => driverById.get(id))
				.filter((driver): driver is PayableDriverBalance => !!driver),
		[excludedIds, driverById]
	);
	const total = useMemo(
		() => sumAmounts(included.map((driver) => driver.balance)),
		[included]
	);

	const trimmedDescription = description.trim();
	const canContinue = included.length > 0 && trimmedDescription.length > 0;

	const exclude = (driverId: number) =>
		setExcludedIds((ids) =>
			ids.includes(driverId) ? ids : [driverId, ...ids]
		);
	const include = (driverId: number) =>
		setExcludedIds((ids) => ids.filter((id) => id !== driverId));

	const handleConfirm = async () => {
		setSubmitting(true);
		try {
			const payout = await payDriverBalances({
				driverIds: included.map((driver) => driver.driverId),
				description: trimmedDescription,
			});
			setResult(payout);
			setStep("result");
		} catch {
			// Toasted by the axios interceptor (e.g. 409 — another payout is running). Stay on the
			// review step so the admin can retry.
		} finally {
			setSubmitting(false);
		}
	};

	const handleClose = () => {
		if (submitting) return;
		if (result && result.paidCount > 0) onPaid();
		onClose();
	};

	const renderFooter = () => {
		if (step === "result" || (!loading && drivers.length === 0)) {
			return (
				<Button type="primary" onClick={handleClose}>
					{copy?.close ?? "Close"}
				</Button>
			);
		}

		if (step === "review") {
			return (
				<>
					<Button
						disabled={submitting}
						onClick={() => setStep("configure")}
					>
						{copy?.back ?? "Back"}
					</Button>
					<Button
						type="primary"
						danger
						loading={submitting}
						onClick={handleConfirm}
					>
						{copy?.confirm ?? "Confirm payment"}
					</Button>
				</>
			);
		}

		return (
			<>
				<Button onClick={handleClose}>
					{copy?.cancel ?? "Cancel"}
				</Button>
				<Button
					type="primary"
					disabled={!canContinue}
					onClick={() => setStep("review")}
				>
					{copy?.continue ?? "Continue"}
				</Button>
			</>
		);
	};

	const renderBody = () => {
		if (loading) {
			return (
				<div className="flex justify-center py-12">
					<Spin size="large" />
				</div>
			);
		}

		if (step === "result" && result) {
			return (
				<PayoutResultStep
					result={result}
					previewBalances={previewBalances}
				/>
			);
		}

		if (drivers.length === 0) {
			return (
				<Empty
					className="py-8"
					image={Empty.PRESENTED_IMAGE_SIMPLE}
					description={
						copy?.emptyState ?? "No driver has a balance to pay"
					}
				/>
			);
		}

		if (step === "review") {
			return (
				<PayoutReviewStep
					driverCount={included.length}
					excludedCount={excluded.length}
					total={total}
					description={trimmedDescription}
				/>
			);
		}

		return (
			<PayoutConfigureStep
				drivers={drivers}
				included={included}
				excluded={excluded}
				total={total}
				description={description}
				onExclude={exclude}
				onInclude={include}
				onDescriptionChange={setDescription}
			/>
		);
	};

	return (
		<ConfigProvider theme={PAYOUT_MODAL_THEME}>
			<Modal
				open={open}
				title={copy?.title ?? "Settle drivers"}
				width={820}
				onCancel={handleClose}
				closable={!submitting}
				keyboard={!submitting}
				maskClosable={false}
				destroyOnClose
				footer={renderFooter()}
			>
				{renderBody()}
			</Modal>
		</ConfigProvider>
	);
};

export default BulkDriverPayoutModal;
